import { Suspense, useEffect, useLayoutEffect, useState } from "react";
import {
  HelmetProvider,
  Helmet,
  type HelmetServerState,
} from "react-helmet-async";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
  matchPath,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster as Sonner } from "@/components/ui/sonner"; // From shadcn/ui
import { ThemeProvider } from "@/components/theme-provider";
import { PrivacyProvider } from "@/contexts/PrivacyContext";
import { PortfolioProvider } from "./contexts/PortfolioContext";
import { AuthListener } from "@/components/AuthListener";
import { lazyImport } from "@/lib/chunkErrorRecovery";
import CookieBanner from "@/components/CookieBanner";
import { useIsMounted } from "@/hooks/useIsMounted";
import { Loader2 } from "lucide-react";
// Side effect import: initialises i18next. Lives here (not main.tsx) so the prerender
// build, which renders App from this file, gets translations too.
import "@/lib/i18n";
import { setLanguage, type Language } from "@/lib/i18n";
import {
  isPublicPath,
  localizePath,
  stripLanguagePrefix,
} from "@/lib/localizedPaths";
import {
  useLanguage,
  useT,
  type TranslationKey,
  type TranslateFn,
} from "@/lib/translation";
import { PublicPageAlternates } from "@/components/site/PublicPageAlternates";

// Import your components
import Layout from "./components/Layout";
import ResearchLayout from "./components/ResearchLayout";
import ProtectedRoute from "./components/ProtectedRoute";
import PaidRoute from "./components/billing/PaidRoute";
import { useSyncLanguageFromSettings } from "./features/settings/hooks/useSyncLanguageFromSettings";

// Lazy loaded pages
const Dashboard = lazyImport(() => import("./pages/Dashboard"));
const SignupPage = lazyImport(() => import("@/pages/site/SignupPage"));
const Holdings = lazyImport(() => import("./pages/Holdings"));
const LoginPage = lazyImport(() => import("./pages/LoginPage"));
const NotFound = lazyImport(() => import("./pages/NotFound"));
const Transactions = lazyImport(() => import("./pages/Transactions"));
const Portfolios = lazyImport(() => import("./pages/Portfolios"));
const Performance = lazyImport(() => import("./pages/Performance"));
const Dividends = lazyImport(() => import("./pages/Dividends"));
const Goals = lazyImport(() => import("./pages/Goals"));
const Roadmap = lazyImport(() => import("./pages/Roadmap"));
const TaxCalculator = lazyImport(() => import("./pages/TaxCalculator"));
const BillingRedirect = lazyImport(() => import("./pages/BillingRedirect"));

// Research Pages
const StockDetail = lazyImport(() => import("./pages/research/StockDetail"));
const StockSearch = lazyImport(() => import("./pages/research/StockSearch"));
const Watchlists = lazyImport(() => import("./pages/research/Watchlists"));
const NewsPage = lazyImport(() => import("./pages/research/NewsPage"));
const EarningsCalendar = lazyImport(
  () => import("./pages/research/EarningsCalendar"),
);
const Transcripts = lazyImport(() => import("./pages/research/Transcripts"));
const ReverseDcf = lazyImport(() => import("./pages/research/ReverseDcf"));
const Charts = lazyImport(() => import("./pages/research/Charts"));

const FeaturesPage = lazyImport(() => import("./pages/site/Features"));
const ResearchHub = lazyImport(() => import("./pages/site/ResearchHub"));
const PublicStockDetail = lazyImport(
  () => import("./pages/site/PublicStockDetail"),
);
const PricingPage = lazyImport(() => import("./pages/site/Pricing"));
const HelpPage = lazyImport(() => import("./pages/site/Help"));
const TermsPage = lazyImport(() => import("./pages/site/Terms"));
const PrivacyPage = lazyImport(() => import("./pages/site/Privacy"));
const DeleteAccountPage = lazyImport(() => import("./pages/site/DeleteAccount"));
const ForgotPasswordPage = lazyImport(
  () => import("./pages/site/ForgotPasswordPage"),
);
const ResetPasswordPage = lazyImport(
  () => import("./pages/site/ResetPasswordPage"),
);
const UnsubscribePage = lazyImport(
  () => import("./pages/site/UnsubscribePage"),
);
const ChangelogPage = lazyImport(() => import("./pages/site/ChangelogPage"));
const ArticlesPage = lazyImport(() => import("./pages/site/ArticlesPage"));
const ArticlePage = lazyImport(() => import("./pages/site/ArticlePage"));
const Trading212GuidePage = lazyImport(
  () => import("./pages/site/guides/Trading212GuidePage"),
);
const IBKRGuidePage = lazyImport(
  () => import("./pages/site/guides/IBKRGuidePage"),
);
const DeGiroGuidePage = lazyImport(
  () => import("./pages/site/guides/DeGiroGuidePage"),
);
const XTBGuidePage = lazyImport(
  () => import("./pages/site/guides/XTBGuidePage"),
);
const RobinhoodGuidePage = lazyImport(
  () => import("./pages/site/guides/RobinhoodGuidePage"),
);

const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Logged in layouts only: applies the language saved in user_settings.
const LanguageSync = () => {
  useSyncLanguageFromSettings();
  return null;
};

const ProtectedLayout = () => (
  <PortfolioProvider>
    <LanguageSync />
    <Layout />
  </PortfolioProvider>
);

// StockDetail (below) needs the selected portfolio to look up buy/sell
// transactions for the price chart, so Analysis Mode needs PortfolioProvider too.
const ProtectedResearchLayout = () => (
  <PortfolioProvider>
    <LanguageSync />
    <ResearchLayout />
  </PortfolioProvider>
);

// useLayoutEffect does nothing during the prerender (and React warns about it there).
const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

// Applies the " · Oitava" tab-title suffix to every public/marketing route in one
// place, so individual pages only ever set their own page-specific title text.
// Wraps the same route tree during both client rendering and prerendering (see
// prerender-entry.tsx, which renders App with a StaticRouter), keeping the two in sync.
// Previously each public page hardcoded its own brand suffix to compensate for
// prerendering not going through App.tsx's top-level Helmet, and client hydration
// then reapplied that top-level Helmet's titleTemplate on top of it, doubling the
// suffix (e.g. "Roadmap · Oitava · Oitava").
const PublicRoutesShell = ({ language }: { language: Language }) => {
  const { pathname } = useLocation();
  const current = useLanguage();
  const isKnownPage = isPublicPath(pathname);

  // The URL decides the language of a public page ("/pt/..." is Portuguese), and
  // becomes the saved choice. Not for unknown paths (the not found page), which
  // keep the current language. Layout effect, so the page never paints in the
  // previous language. A Portuguese visitor landing on an English URL is sent to
  // the Portuguese URL before anything is drawn, by /language-redirect.js.
  useIsomorphicLayoutEffect(() => {
    if (isKnownPage && current !== language) setLanguage(language);
  }, [isKnownPage, language, current]);

  return (
    <>
      <Helmet titleTemplate="%s · Oitava" />
      <PublicPageAlternates language={language} />
      <Outlet />
    </>
  );
};

// One copy of the public routes per language. Same pages, the paths differ only
// by the language prefix.
const renderPublicRoutes = (language: Language) => {
  const path = (p: string) => localizePath(p, language);
  return (
    <>
      <Route path={path("/")} element={<FeaturesPage />} />
      <Route path={path("/research")} element={<ResearchHub />} />
      <Route path={path("/research/:ticker")} element={<PublicStockDetail />} />
      <Route path={path("/pricing")} element={<PricingPage />} />
      <Route path={path("/help")} element={<HelpPage />} />
      <Route path={path("/changelog")} element={<ChangelogPage />} />
      <Route path={path("/articles")} element={<ArticlesPage />} />
      <Route path={path("/articles/:slug")} element={<ArticlePage />} />
      <Route
        path={path("/help/trading-212")}
        element={<Trading212GuidePage />}
      />
      <Route
        path={path("/help/interactive-brokers")}
        element={<IBKRGuidePage />}
      />
      <Route path={path("/help/degiro")} element={<DeGiroGuidePage />} />
      <Route path={path("/help/xtb")} element={<XTBGuidePage />} />
      <Route path={path("/help/robinhood")} element={<RobinhoodGuidePage />} />
      <Route path={path("/terms")} element={<TermsPage />} />
      <Route path={path("/privacy")} element={<PrivacyPage />} />
      <Route path={path("/delete-account")} element={<DeleteAccountPage />} />
      <Route path={path("/login")} element={<LoginPage />} />
      <Route path={path("/signup")} element={<SignupPage />} />
      <Route path={path("/forgot-password")} element={<ForgotPasswordPage />} />
      <Route path={path("/reset-password")} element={<ResetPasswordPage />} />
      <Route path={path("/unsubscribe")} element={<UnsubscribePage />} />
      <Route path={language === "en" ? "*" : "/pt/*"} element={<NotFound />} />
    </>
  );
};

// Every route's tab title, resolved straight from the URL. This is what lets
// RouteTitle (below) show the destination page's title the instant a link is
// clicked, before the route's lazy chunk has even loaded. No per-page Helmet
// can do that, since it only mounts once its chunk arrives. Params-dependent
// titles (ticker pages) use the ticker itself, which is already known from the
// URL alone. Routes not listed here (redirects, "*" catch-alls, /app billing
// bounce) are rare/instant enough that the App-level lastRealTitle fallback
// covers them adequately.
const ROUTE_TITLES: {
  path: string;
  title:
    | TranslationKey
    | ((params: Record<string, string | undefined>, t: TranslateFn) => string);
  suffixed: boolean;
}[] = [
  {
    path: "/",
    title: "site:features.metaTitle",
    suffixed: false,
  },
  {
    path: "/research",
    title: "site:researchHub.metaTitle",
    suffixed: true,
  },
  {
    path: "/research/:ticker",
    title: (p, t) =>
      p.ticker ? p.ticker.toUpperCase() : t("nav:items.stockAnalysis"),
    suffixed: true,
  },
  { path: "/pricing", title: "nav:marketing.pricing", suffixed: true },
  { path: "/help", title: "site:help.metaTitle", suffixed: true },
  { path: "/changelog", title: "nav:marketing.changelog", suffixed: true },
  { path: "/articles", title: "nav:marketing.articles", suffixed: true },
  { path: "/articles/:slug", title: "nav:marketing.articles", suffixed: true },
  {
    path: "/help/trading-212",
    title: "site:guides.trading212.metaTitle",
    suffixed: true,
  },
  {
    path: "/help/interactive-brokers",
    title: "site:guides.ibkr.metaTitle",
    suffixed: true,
  },
  {
    path: "/help/degiro",
    title: "site:guides.degiro.metaTitle",
    suffixed: true,
  },
  {
    path: "/help/xtb",
    title: "site:guides.xtb.metaTitle",
    suffixed: true,
  },
  {
    path: "/help/robinhood",
    title: "site:guides.robinhood.metaTitle",
    suffixed: true,
  },
  { path: "/terms", title: "nav:routeTitles.terms", suffixed: true },
  { path: "/privacy", title: "nav:routeTitles.privacy", suffixed: true },
  { path: "/delete-account", title: "nav:routeTitles.deleteAccount", suffixed: true },
  { path: "/login", title: "auth:login.metaTitle", suffixed: true },
  { path: "/signup", title: "auth:signup.metaTitle", suffixed: true },
  { path: "/forgot-password", title: "auth:forgot.title", suffixed: true },
  { path: "/reset-password", title: "auth:reset.metaTitle", suffixed: true },
  { path: "/unsubscribe", title: "site:unsubscribe.metaTitle", suffixed: true },
  {
    path: "/portfolio/dashboard",
    title: "nav:items.dashboard",
    suffixed: false,
  },
  {
    path: "/portfolio/portfolios",
    title: "nav:items.portfolios",
    suffixed: false,
  },
  { path: "/portfolio/holdings", title: "nav:items.holdings", suffixed: false },
  {
    path: "/portfolio/performance",
    title: "nav:items.performance",
    suffixed: false,
  },
  {
    path: "/portfolio/dividends",
    title: "nav:items.dividends",
    suffixed: false,
  },
  { path: "/portfolio/goals", title: "nav:items.goals", suffixed: false },
  {
    path: "/portfolio/portfolios/:portfolioId",
    title: "nav:items.holdings",
    suffixed: false,
  },
  {
    path: "/portfolio/portfolios/:portfolioId/transactions",
    title: "nav:items.transactions",
    suffixed: false,
  },
  {
    path: "/portfolio/transactions",
    title: "nav:items.transactions",
    suffixed: false,
  },
  {
    path: "/portfolio/taxes",
    title: "nav:items.taxCalculator",
    suffixed: false,
  },
  {
    path: "/portfolio/roadmap",
    title: "common:sidebar.roadmap",
    suffixed: false,
  },
  {
    path: "/analysis/stock-analysis",
    title: "nav:items.stockAnalysis",
    suffixed: false,
  },
  {
    path: "/analysis/stock-analysis/:ticker",
    title: (p, t) =>
      p.ticker ? p.ticker.toUpperCase() : t("nav:items.stockAnalysis"),
    suffixed: false,
  },
  {
    path: "/analysis/watchlist",
    title: "research:watchlists.metaTitle",
    suffixed: false,
  },
  { path: "/analysis/news", title: "research:news.metaTitle", suffixed: false },
  {
    path: "/analysis/earnings-calendar",
    title: "nav:items.earningsCalendar",
    suffixed: false,
  },
  {
    path: "/analysis/transcripts",
    title: "research:transcripts.title",
    suffixed: false,
  },
  {
    path: "/analysis/reverse-dcf",
    title: "nav:items.dcfCalculators",
    suffixed: false,
  },
  { path: "/analysis/charts", title: "nav:items.charts", suffixed: false },
  {
    path: "/analysis/roadmap",
    title: "common:sidebar.roadmap",
    suffixed: false,
  },
];

// Always mounted (outside Suspense), so it survives lazy-chunk loading and sets
// the tab title synchronously on every navigation, before the destination page's
// own Helmet exists. Once the page's chunk loads, its own Helmet renders the same
// text (or refines it, e.g. an article's real headline) with no visible change.
const RouteTitle = () => {
  const t = useT();
  const location = useLocation();
  // "/pt/pricing" has the same title entry as "/pricing".
  const pathname = stripLanguagePrefix(location.pathname);
  const entry = ROUTE_TITLES.find((candidate) =>
    matchPath({ path: candidate.path, end: true }, pathname),
  );
  if (!entry) return null;

  const params =
    matchPath({ path: entry.path, end: true }, pathname)?.params ?? {};
  const text =
    typeof entry.title === "function" ? entry.title(params, t) : t(entry.title);

  return (
    <Helmet>
      <title>{entry.suffixed ? `${text} · Oitava` : text}</title>
    </Helmet>
  );
};

// Top-level path segments that belong to the authenticated app. Everything
// else (site/marketing pages, auth pages, public research pages) is
// unauthenticated and must always render in light mode, regardless of the
// signed-in user's saved theme preference.
const PROTECTED_PATH_SEGMENTS = new Set([
  "portfolio",
  "analysis",
  "app",
  "dashboard",
  "portfolios",
  "holdings",
  "performance",
  "dividends",
  "transactions",
]);

// Wraps ThemeProvider so it can read the current route and force light mode
// on public pages. Uses next-themes' `forcedTheme`, which only overrides the
// applied DOM class/attribute. Unlike calling setTheme(), it never touches
// localStorage, so it can't leak into other tabs or clobber the signed-in
// user's actual saved preference (next-themes broadcasts every setTheme()
// call to other tabs via a `storage` event listener, which is what caused
// that leak before).
const AppThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const firstSegment = location.pathname.split("/")[1] ?? "";
  const isProtectedRoute = PROTECTED_PATH_SEGMENTS.has(firstSegment);

  return (
    <ThemeProvider
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange
      attribute="class"
      scriptProps={{ "data-cfasync": "false" }}
      forcedTheme={isProtectedRoute ? undefined : "light"}
    >
      {children}
    </ThemeProvider>
  );
};

// Sonner/AuthListener/CookieBanner read `document`/`window`/`localStorage` in their
// render body, so they can't render during prerendering. They also must not render on
// the client's hydration pass, or the client tree won't match the prerendered markup
// (React #418, then #423 as it throws away the server HTML and client renders the whole
// root). Rendering them only after mount keeps both first renders identical. They stay
// nested here because they need Router + QueryClientProvider context.
const BrowserOnlyChrome = () => {
  const isMounted = useIsMounted();
  if (!isMounted) return null;

  return (
    <>
      <Sonner />
      <AuthListener />
      <CookieBanner />
    </>
  );
};

const AppRoutes = () => {
  const t = useT();
  return (
    <AppThemeProvider>
      <PrivacyProvider>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider delayDuration={0}>
            <BrowserOnlyChrome />
            <RouteTitle />
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                {/* --- Public site routes (unauthenticated), always light themed, see AppThemeProvider.
                    English at "/...", Portuguese at "/pt/...", same pages. --- */}
                <Route element={<PublicRoutesShell language="en" />}>
                  {renderPublicRoutes("en")}
                </Route>
                <Route element={<PublicRoutesShell language="pt-PT" />}>
                  {renderPublicRoutes("pt-PT")}
                </Route>

                {/* --- Authenticated app --- */}
                <Route element={<ProtectedRoute />}>
                  {/* Portfolio Mode */}
                  <Route path="/portfolio" element={<ProtectedLayout />}>
                    <Route path="dashboard" element={<Dashboard />} />
                    <Route path="portfolios" element={<Portfolios />} />
                    <Route path="holdings" element={<Holdings />} />
                    <Route path="performance" element={<Performance />} />
                    <Route path="dividends" element={<Dividends />} />
                    <Route
                      path="goals"
                      element={
                        <PaidRoute
                          allowDemoPreview
                          title={t("nav:items.goals")}
                          description={t("nav:paidRoutes.goalsDescription")}
                        >
                          <Goals />
                        </PaidRoute>
                      }
                    />
                    <Route
                      path="portfolios/:portfolioId"
                      element={<Holdings />}
                    />
                    <Route
                      path="portfolios/:portfolioId/transactions"
                      element={<Transactions />}
                    />
                    <Route path="transactions" element={<Transactions />} />
                    <Route
                      path="taxes"
                      element={
                        <PaidRoute
                          allowDemoPreview
                          title={t("nav:items.taxCalculator")}
                          description={t("nav:paidRoutes.taxDescription")}
                        >
                          <TaxCalculator />
                        </PaidRoute>
                      }
                    />
                    <Route path="roadmap" element={<Roadmap />} />
                  </Route>

                  {/* Analysis Mode */}
                  <Route path="/analysis" element={<ProtectedResearchLayout />}>
                    <Route path="stock-analysis" element={<StockSearch />} />
                    <Route
                      path="stock-analysis/:ticker"
                      element={<StockDetail />}
                    />
                    <Route path="watchlist" element={<Watchlists />} />
                    <Route
                      path="news"
                      element={
                        <PaidRoute
                          title={t("nav:paidRoutes.newsTitle")}
                          description={t("nav:paidRoutes.newsDescription")}
                        >
                          <NewsPage />
                        </PaidRoute>
                      }
                    />
                    <Route
                      path="earnings-calendar"
                      element={
                        <PaidRoute
                          title={t("nav:items.earningsCalendar")}
                          description={t("nav:paidRoutes.earningsDescription")}
                        >
                          <EarningsCalendar />
                        </PaidRoute>
                      }
                    />
                    <Route
                      path="transcripts"
                      element={
                        <PaidRoute
                          title={t("research:transcripts.title")}
                          description={t(
                            "nav:paidRoutes.transcriptsDescription",
                          )}
                        >
                          <Transcripts />
                        </PaidRoute>
                      }
                    />
                    <Route
                      path="reverse-dcf"
                      element={
                        <PaidRoute
                          title={t("nav:items.dcfCalculators")}
                          description={t("nav:paidRoutes.dcfDescription")}
                        >
                          <ReverseDcf />
                        </PaidRoute>
                      }
                    />
                    <Route
                      path="charts"
                      element={
                        <PaidRoute
                          title={t("nav:items.charts")}
                          description={t("nav:paidRoutes.chartsDescription")}
                        >
                          <Charts />
                        </PaidRoute>
                      }
                    />
                    <Route path="roadmap" element={<Roadmap />} />
                  </Route>

                  {/* Stripe success_url/cancel_url land here (?billing=success|cancel) */}
                  <Route path="/app" element={<BillingRedirect />} />

                  {/* Redirects for legacy routes */}
                  <Route
                    path="/dashboard"
                    element={<Navigate to="/portfolio/dashboard" replace />}
                  />
                  <Route
                    path="/portfolios"
                    element={<Navigate to="/portfolio/portfolios" replace />}
                  />
                  <Route
                    path="/portfolios/:portfolioId"
                    element={
                      <Navigate
                        to="/portfolio/portfolios/:portfolioId"
                        replace
                      />
                    }
                  />
                  <Route
                    path="/portfolios/:portfolioId/transactions"
                    element={
                      <Navigate
                        to="/portfolio/portfolios/:portfolioId/transactions"
                        replace
                      />
                    }
                  />
                  <Route
                    path="/holdings"
                    element={<Navigate to="/portfolio/holdings" replace />}
                  />
                  <Route
                    path="/performance"
                    element={<Navigate to="/portfolio/performance" replace />}
                  />
                  <Route
                    path="/dividends"
                    element={<Navigate to="/portfolio/dividends" replace />}
                  />
                  <Route
                    path="/transactions"
                    element={<Navigate to="/portfolio/transactions" replace />}
                  />
                </Route>
              </Routes>
            </Suspense>
          </TooltipProvider>
        </QueryClientProvider>
      </PrivacyProvider>
    </AppThemeProvider>
  );
};

interface AppProps {
  // Set only by the prerender script (fresh object per route render) so react-helmet-async
  // can be read back after renderToPipeableStream completes. Inert/unused on the client,
  // which relies on HelmetProvider's own default internal context instead.
  helmetContext?: { helmet?: HelmetServerState };
  // The prerender script passes a StaticRouter bound to the route it renders; the
  // client uses BrowserRouter. Both render this same App tree so React's useId values
  // (Radix ids, aria-controls) match between the prerendered HTML and hydration.
  Router?: React.ComponentType<{ children: React.ReactNode }>;
}

const BARE_TITLE = "Oitava";

// Suspense's fallback (during a lazy route chunk load) replaces the whole route tree,
// including PublicRoutesShell and every page's own <Helmet>, so with a static
// defaultTitle, the tab briefly reverts to plain "Oitava" on every navigation. Tracking
// document.title and feeding the last real title back in as defaultTitle means the
// fallback keeps showing the page you're leaving instead of flashing the bare brand name.
const useLastRealTitle = () => {
  const [title, setTitle] = useState(BARE_TITLE);

  useEffect(() => {
    const titleEl = document.querySelector("title");
    if (!titleEl) return;
    const observer = new MutationObserver(() => {
      if (document.title && document.title !== BARE_TITLE) {
        setTitle(document.title);
      }
    });
    observer.observe(titleEl, {
      childList: true,
      characterData: true,
      subtree: true,
    });
    return () => observer.disconnect();
  }, []);

  return title;
};

// v7_startTransition runs every navigation as a React transition, so the current
// page stays on screen while the next page's lazy chunk loads, instead of
// switching to a Suspense spinner straight away.
const ClientRouter = ({ children }: { children: React.ReactNode }) => (
  <BrowserRouter future={{ v7_startTransition: true }}>
    {children}
  </BrowserRouter>
);

const App = ({ helmetContext = {}, Router = ClientRouter }: AppProps) => {
  const lastRealTitle = useLastRealTitle();

  return (
    <HelmetProvider context={helmetContext}>
      {/* Fallback only, never rendered over an app route, which all set their own
          <title> (protected/app pages plain, public pages via PublicRoutesShell's
          titleTemplate). */}
      <Helmet defaultTitle={lastRealTitle}>
        <meta property="og:site_name" content="Oitava" />
      </Helmet>
      <Router>
        <AppRoutes />
      </Router>
    </HelmetProvider>
  );
};
export default App;
